import React from "react";
import VariableArrayRow from "./VariableArrayRow";
import { Button, Col, FormFeedback, Input, Row } from "reactstrap";

/**
 * bottomblockright building block (SCRUM-642, VariableField registry) —
 * repeatable items, each a logo (Link URL input / Upload file input toggled
 * by radios, upload base64-encoded via FileReader) plus a description
 * textarea, wrapped in a <details> whose <summary> is the variable name.
 *
 * Byte-identical extraction of the three legacy surfaces:
 * - create (wizard): IndexTemplateSelector.jsx bottomblockright branch
 *   (ContentOptional Droppable only) — pinned by SCRUM-630
 *   tests/integration/pages/contentVariables.types.test.jsx.
 * - update: the former Required host (.jsx) / the former Optional host (.jsx) branches
 *   (character-identical to each other) — pinned by SCRUM-640
 *   handleContentRequired.composites.test.jsx /
 *   handleContentOptional.types.test.jsx, and the stored-at-publish shape by
 *   e2e/tests/variableBaselineComposites.spec.ts (SCRUM-746).
 *
 * Emission: `onChange(settingVar.variable, { items: [...] })` — the canonical
 * page_content value, identical JSON on every surface. Host adapters
 * (SCRUM-643) rewrap it: the wizard into
 * `onChangeVariableValue({ target: { name, value } })` (which mutates the
 * Content entry in place — existing host behaviour, keep it there), the
 * update hosts into `setVariables` + `props.onVariableChange({ ...v, value })`.
 * HandleContent (optional) reports through onVariableChange for this type, NOT
 * through onChangeContentOptional (that channel is sitemap's).
 *
 * Variant differences (variant="create" = wizard, "update" = both Handle files):
 * - <details>: create `className="bottomblockright-container" data-collapsible`
 *   (collapsed by default, data-collapsible is decorative); update
 *   `className="bottomblockright-container mb-3" open` (the legacy
 *   `key={index}` is left to the parent map).
 * - <summary>: create spreads `summaryProps` (replaces the wizard's guarded
 *   `dragProvided.dragHandleProps` spread — the step-4 host passes its drag
 *   handle props; the legacy outer-div double-spread stays host chrome);
 *   update has no spread.
 * - remove button: create never disabled, inside
 *   `<Col md="1" className="d-flex justify-content-end">`; update
 *   `disabled={value.items.length === 1}`, plain `<Col md="1">`.
 * - empty-logo placeholder: create `No logo yet` (inline style span); update
 *   `No logo` (`text-muted` span).
 * - description textarea: create rows={4}, `value={item.description}` (no
 *   fallback), `is-invalid` className hook; update rows={3},
 *   `value={item.description || ''}`, no className.
 * - description Row: create `varible_container`; update `varible_container mt-2`.
 * - link Input: create carries the `is-invalid` className hook; update none.
 * - add-item Row: create `mt-3`; update `mt-4`.
 * - validation chrome (create only): `validationError` +
 *   `isValidationAttempted` props reproduce the wizard's
 *   `validationErrors[fieldName]` / `props.isValidationAttempted` JSX exactly
 *   (dead in practice — the wizard only validates ContentRequired and this
 *   type renders in ContentOptional — but preserved).
 *
 * Preserved quirks:
 * - Normalize-on-next-edit: handlers write PARTIAL logo objects
 *   ({type:'link',url} / {type:'upload',name,base64}); `normalizeItems`
 *   re-expands any object logo to the full {type,url,name,base64} shape when
 *   the value is re-read on the next render. First edit emits the partial
 *   logo (unit-test pinned); any later edit emits the full 4-key logo
 *   (e2e stored-shape pinned). Do not normalize eagerly on emit.
 * - Radio toggle REPLACES the logo: link→upload discards url, upload→link
 *   discards name/base64; the handler does not check the current type, so a
 *   programmatic same-value change event would also reset.
 * - Remove is unguarded in create (items can reach 0, emitting {items:[]});
 *   update disables the button at length 1 — but a wizard-emptied {items:[]}
 *   stays at zero rows on the update surfaces (no re-seeding), so `+ Add item`
 *   is the only way back.
 * - Value ""/null/non-object → ONE blank item; {items:[]}, bare [], or an
 *   object without `items` → ZERO items; legacy string logo → link url;
 *   falsy logo.type on an object logo → 'link'.
 * - Index-based item keys (removal re-keys; a stale uncontrolled file input
 *   selection after removing a middle item is existing behaviour).
 * - No per-site [{siteid,label}] unwrap — the legacy code has none here.
 */
export type BottomBlockRightFieldProps = {
  /** the stored page_content value is untyped JSON; this block normalises it below */
  settingVar: { variable?: string; value?: any }; // any: stored page_content value; legacy items carry partial or full logo objects, normalized below
  /** reports the canonical value object for the variable */
  onChange: (name: string | undefined, value: Record<string, unknown>) => void;
  variant?: "create" | "update";
  summaryProps?: Record<string, unknown>;
  validationError?: React.ReactNode;
  isValidationAttempted?: boolean;
};

export default function BottomBlockRightField({
  settingVar,
  onChange,
  variant = "update",
  summaryProps = {},
  validationError,
  isValidationAttempted = false,
}: BottomBlockRightFieldProps) {
  const fieldName = settingVar.variable;

  // Normalize incoming value to: { items: [{ logo:{type:'link'|'upload', url?, name?, base64?}, description:'' }] }
  const normalizeItems = (arr) =>
    (Array.isArray(arr) ? arr : []).map((it) => ({
      logo:
        it && typeof it.logo === "object"
          ? { type: it.logo.type || "link", url: it.logo.url || "", name: it.logo.name || "", base64: it.logo.base64 || "" }
          : { type: "link", url: (it && it.logo) || "" },
      description: (it && it.description) || "",
    }));

  const initial =
    settingVar.value && typeof settingVar.value === "object"
      ? { items: normalizeItems(settingVar.value.items) }
      : Array.isArray(settingVar.value)
      ? { items: normalizeItems(settingVar.value) }
      : { items: [{ logo: { type: "link", url: "" }, description: "" }] };

  const value = initial;

  const setValue = (next) => onChange(fieldName, next);

  const setItems = (nextItems) => setValue({ ...value, items: nextItems });

  const updateDesc = (idx) => (e) => {
    const items = value.items.map((it, i) =>
      i === idx ? { ...it, description: e.target.value } : it
    );
    setItems(items);
  };

  const setLogoSourceType = (idx) => (e) => {
    const nextType = e.target.value; // 'link' | 'upload'
    const items = value.items.map((it, i) =>
      i === idx
        ? {
            ...it,
            logo:
              nextType === "link"
                ? { type: "link", url: "" }
                : { type: "upload", name: "", base64: "" },
          }
        : it
    );
    setItems(items);
  };

  const onLogoLinkChange = (idx) => (e) => {
    const items = value.items.map((it, i) =>
      i === idx ? { ...it, logo: { type: "link", url: e.target.value } } : it
    );
    setItems(items);
  };

  const onLogoFileChange = (idx) => (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const items = value.items.map((it, i) =>
        i === idx ? { ...it, logo: { type: "upload", name: file.name, base64: reader.result } } : it
      );
      setItems(items);
    };
    reader.readAsDataURL(file);
  };

  const addItem = () =>
    setItems([...value.items, { logo: { type: "link", url: "" }, description: "" }]);

  const removeItem = (idx) => setItems(value.items.filter((_, i) => i !== idx));

  // Helper: choose the correct preview src
  const logoSrc = (logo) =>
    logo?.type === "upload" ? logo.base64 || "" : logo?.url || "";

  // SCRUM-2121: the logo cells and the description textarea of a row, shared
  // by the create and update renderers below. What differs per variant is
  // passed in: the link input's invalid class (create only, `""` when valid),
  // the empty-logo placeholder element, and the textarea's rows and class.
  const logoCells = (item, i, linkClassName: string | undefined, emptyLogo: React.ReactNode) => (
    <>
      <Col md="6">
        <div className="mb-2">
          <label style={{ marginRight: 12 }}>
            <input type="radio" name={`${fieldName}[${i}]-logo-source`} value="link" checked={item.logo?.type === "link"} onChange={setLogoSourceType(i)} /> Link
          </label>
          <label>
            <input type="radio" name={`${fieldName}[${i}]-logo-source`} value="upload" checked={item.logo?.type === "upload"} onChange={setLogoSourceType(i)} /> Upload
          </label>
        </div>
        {item.logo?.type === "link" ? (
          <Input type="text" placeholder="https://example.com/logo.png" value={item.logo?.url || ""} onChange={onLogoLinkChange(i)} name={`${fieldName}[${i}].logo.url`} className={linkClassName} />
        ) : (
          <Input type="file" accept="image/*" onChange={onLogoFileChange(i)} name={`${fieldName}[${i}].logo.file`} />
        )}
        {item.logo?.type === "upload" && item.logo?.name && <small className="text-muted d-block mt-1">{item.logo.name}</small>}
      </Col>
      <Col md="3">
        {logoSrc(item.logo) ? (
          <img src={logoSrc(item.logo)} alt={`logo-${i}`} style={{ maxWidth: "100%", maxHeight: 60, objectFit: "contain", borderRadius: 6 }} />
        ) : (
          emptyLogo
        )}
      </Col>
    </>
  );

  const descArea = (i: number, rows: number, text: string, className: string | undefined) => ({
    rows,
    placeholder: "Type the description…",
    value: text,
    onChange: updateDesc(i),
    name: `${fieldName}[${i}].description`,
    className,
  });

  if (variant === "create") {
    return (
      <details className="bottomblockright-container" data-collapsible>
        <summary
          className="varible_container form-control-label d-flex align-items-center"
          {...summaryProps}
          style={{ cursor: "pointer", userSelect: "none" }}
        >
          {fieldName}
        </summary>

        <div className="pt-3">
          {value.items.map((item, i) => (
            // index key: controlled row, no row-local state (SCRUM-2183)
            <VariableArrayRow key={i} className="bbr-item mb-3" topRowClassName="varible_container align-items-center"
              topLabel={`Logo #${i + 1}`} onRemove={() => removeItem(i)} removeColClassName="d-flex justify-content-end"
              bottomRowClassName="varible_container" bottomLabel={`Description #${i + 1}`}
              textarea={descArea(i, 4, item.description, validationError ? "is-invalid" : "")}>
              {logoCells(item, i, validationError ? "is-invalid" : "", <span style={{ fontSize: 12, color: "#888" }}>No logo yet</span>)}
            </VariableArrayRow>
          ))}

          <Row className="mt-3">
            <Col md={{ offset: 2, size: 10 }}>
              <Button type="button" color="primary" onClick={addItem}>
                + Add item
              </Button>
            </Col>
          </Row>

          {isValidationAttempted && validationError && (
            <Row className="mt-2">
              <Col md={{ offset: 2, size: 10 }}>
                <FormFeedback className="medium-text d-block">
                  {validationError}
                </FormFeedback>
              </Col>
            </Row>
          )}
        </div>
      </details>
    );
  }

  return (
    <details className="bottomblockright-container mb-3" open>
      <summary className="varible_container form-control-label d-flex align-items-center" style={{ cursor: "pointer", userSelect: "none" }}>
        {fieldName}
      </summary>
      <div className="pt-3">
        {value.items.map((item, i) => (
          // index key: controlled row, no row-local state (SCRUM-2183)
          <VariableArrayRow key={i} className="bbr-item mb-3" topRowClassName="varible_container align-items-center"
            topLabel={`Logo #${i + 1}`} onRemove={() => removeItem(i)} removeDisabled={value.items.length === 1}
            bottomRowClassName="varible_container mt-2" bottomLabel={`Description #${i + 1}`}
            textarea={descArea(i, 3, item.description || "", undefined)}>
            {logoCells(item, i, undefined, <span className="text-muted">No logo</span>)}
          </VariableArrayRow>
        ))}
        <Row className="mt-4">
          <Col md={{ offset: 2, size: 10 }}>
            <Button type="button" color="primary" onClick={addItem}>+ Add item</Button>
          </Col>
        </Row>
      </div>
    </details>
  );
}

// renders its own <summary>/header chrome - the dispatcher skips its label row (SCRUM-643)
BottomBlockRightField.ownsChrome = true;
